// src/pages/Admin/Payments.jsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, TextField, InputAdornment, Avatar,
  Select, FormControl, InputLabel, MenuItem, Tooltip, LinearProgress, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Alert,
} from '@mui/material';
import {
  Search, Refresh, Visibility, CheckCircle, Cancel,
  AttachMoney, Payment, Warning, Download, OpenInNew,
  NotificationsActive, WhatsApp, Email,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { formatCurrency, formatDate, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

// Resolve payment proof URL from whichever field the backend returns
const getProofUrl = (payment) => {
  const raw =
    payment?.proof_url ||
    payment?.payment_proof_url ||
    payment?.proof_path ||
    payment?.file_url ||
    payment?.document_url ||
    payment?.slip_url ||
    payment?.receipt_url ||
    payment?.attachment_url ||
    null;
  if (!raw) return null;
  if (raw.startsWith('http')) return raw;
  return `${API_BASE}/${raw.replace(/^\//, '')}`;
};

const AdminPayments = () => {
  const [payments,    setPayments]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [tabValue,    setTabValue]    = useState(0);
  const [methodFilter,setMethodFilter]= useState('all');
  const [selected,    setSelected]    = useState(null);
  const [viewDialog,  setViewDialog]  = useState(false);
  const [notifyDialog,setNotifyDialog]= useState(false);
  const [notifyMsg,   setNotifyMsg]   = useState('');
  const [notifyResult,setNotifyResult]= useState(null);
  const [notifying,   setNotifying]   = useState(false);

  const TAB_STATUS_MAP = ['all', 'pending', 'verified', 'failed', 'refunded'];

  useEffect(() => { fetchPayments(); }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllPayments();

      // ✅ FIX: backend returns plain array
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.payments || response.data?.data || []);

      setPayments(data);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (id) => {
    try {
      await adminAPI.verifyPayment(id);
      toast.success('Payment verified');
      fetchPayments();
    } catch { toast.error('Failed to verify payment'); }
  };

  const openNotifyForPayment = (payment) => {
    setNotifyResult(null);
    const statusLabel = (payment.status || 'pending').toUpperCase();
    const defaultMsg = `Your payment of ${payment.amount ? `$${payment.amount}` : ''} for Enquiry #${payment.enquiry?.id || payment.enquiry_id || '—'} has been ${statusLabel === 'VERIFIED' ? 'confirmed ✓' : statusLabel === 'FAILED' ? 'rejected ✗' : 'received and is under review'}. Transaction ID: ${payment.transaction_id || payment.id}.`;
    setNotifyMsg(defaultMsg);
    setNotifyDialog(true);
  };

  const handleNotify = async () => {
    if (!selected) return;
    setNotifying(true);
    try {
      const buyerId = selected.buyer_id || selected.buyerId || selected.user?.id || selected.user_id;
      const res = await adminAPI.notifyBuyer({
        buyer_id: buyerId,
        message: notifyMsg,
        type: 'payment_update',
      });
      setNotifyResult(res.data);
      toast.success('Notification links ready!');
    } catch (err) {
      toast.error('Failed to get notification details');
    } finally {
      setNotifying(false);
    }
  };

  const handleReject = async (id) => {
    try {
      await adminAPI.rejectPayment(id, { reason: 'Rejected by admin' });
      toast.success('Payment rejected');
      fetchPayments();
    } catch { toast.error('Failed to reject payment'); }
  };

  const stats = {
    total:     payments.length,
    totalAmt:  payments.reduce((s, p) => s + (p.amount || 0), 0),
    pending:   payments.filter(p => p.status === 'pending').length,
    pendingAmt:payments.filter(p => p.status === 'pending').reduce((s, p) => s + (p.amount || 0), 0),
    verified:  payments.filter(p => p.status === 'verified').length,
    verifiedAmt:payments.filter(p => p.status === 'verified').reduce((s, p) => s + (p.amount || 0), 0),
  };

  const statusFilter = TAB_STATUS_MAP[tabValue];
  const filteredPayments = payments.filter(p => {
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    const matchesMethod = methodFilter === 'all' || p.payment_method === methodFilter;
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      !q ||
      (p.transaction_id || '').toLowerCase().includes(q) ||
      (p.buyerName      || '').toLowerCase().includes(q) ||
      (p.buyerEmail     || '').toLowerCase().includes(q) ||
      (p.enquiry?.id    || '').toString().includes(q);
    return matchesStatus && matchesMethod && matchesSearch;
  });

  if (loading) return (
    <Container sx={{ py: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography>Loading payments...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Payments Management</Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and verify all payment transactions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Download />}
            onClick={() => toast.success('Export coming soon')}>Export Report</Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={fetchPayments}>Refresh</Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Payments',      value: stats.total,                     color: '#2196f3', icon: <Payment /> },
          { label: 'Total Amount',        value: formatCurrency(stats.totalAmt),  color: '#4caf50', icon: <AttachMoney />, isText: true, textColor: 'success.main' },
          { label: 'Pending Verification',value: stats.pending,                   color: '#ff9800', icon: <Warning />,     textColor: 'warning.main',
            sub: formatCurrency(stats.pendingAmt) },
          { label: 'Verified Payments',   value: stats.verified,                  color: '#4caf50', icon: <CheckCircle />, textColor: 'success.main',
            sub: formatCurrency(stats.verifiedAmt) },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                    <Typography variant="h4" fontWeight="bold" color={s.textColor || 'text.primary'}
                      sx={s.isText ? { fontSize: '1.4rem' } : {}}>
                      {s.value}
                    </Typography>
                    {s.sub && <Typography variant="caption" color="text.secondary">{s.sub}</Typography>}
                  </Box>
                  <Avatar sx={{ bgcolor: `${s.color}20`, color: s.color }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable">
          {['All', 'Pending', 'Verified', 'Failed', 'Refunded'].map((label, i) => (
            <Tab key={i} label={label} />
          ))}
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small"
              placeholder="Search by enquiry ID, buyer, or transaction..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Method</InputLabel>
              <Select label="Payment Method" value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
                <MenuItem value="all">All Methods</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="credit_card">Credit Card</MenuItem>
                <MenuItem value="paypal">PayPal</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                {['Transaction', 'Buyer', 'Amount', 'Method', 'Status', 'Date', 'Actions'].map(h => (
                  <TableCell key={h}><Typography variant="subtitle2" fontWeight="bold">{h}</Typography></TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Payment sx={{ fontSize: 60, color: 'text.secondary', display: 'block', mx: 'auto', mb: 1 }} />
                    <Typography color="text.secondary">
                      {payments.length === 0 ? 'No payments found' : 'No payments match the filter'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredPayments.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      #{p.transaction_id || p.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Enquiry #{p.enquiry?.id || p.enquiry_id || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
                        {(p.buyerName || p.user?.company_name || 'B').charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {p.buyerName || p.user?.company_name || p.user?.email || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.buyerEmail || p.user?.email || ''}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(p.amount || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {(p.payment_method || '—').replace('_', ' ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={p.status || 'pending'} size="small"
                      color={getStatusColor(p.status)}
                      sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(p.created_at || p.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" color="primary"
                          onClick={() => { setSelected(p); setViewDialog(true); }}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Notify Buyer">
                        <IconButton size="small" color="secondary"
                          onClick={() => { setSelected(p); openNotifyForPayment(p); }}>
                          <NotificationsActive />
                        </IconButton>
                      </Tooltip>
                      {p.status === 'pending' && (
                        <>
                          <Tooltip title="Verify Payment">
                            <IconButton size="small" color="success" onClick={() => handleVerify(p.id)}>
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject Payment">
                            <IconButton size="small" color="error" onClick={() => handleReject(p.id)}>
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredPayments.length} of {payments.length} payments
          </Typography>
        </Box>
      </Paper>

      {/* View Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        {selected && (() => {
          const proofUrl = getProofUrl(selected);
          const isImage  = proofUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(proofUrl);
          const isPdf    = proofUrl && /\.pdf$/i.test(proofUrl);
          return (
            <>
              <DialogTitle>Payment Details</DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  {[
                    ['Transaction ID', selected.transaction_id || selected.id],
                    ['Amount',         formatCurrency(selected.amount)],
                    ['Method',         selected.payment_method],
                    ['Status',         selected.status],
                    ['Buyer',          selected.buyerName || selected.user?.company_name],
                    ['Email',          selected.buyerEmail || selected.user?.email],
                    ['Enquiry ID',     selected.enquiry_id],
                    ['Date',           formatDate(selected.created_at)],
                  ].map(([label, value]) => value ? (
                    <Box key={label} sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ width: 120, flexShrink: 0 }}>
                        {label}:
                      </Typography>
                      <Typography variant="body2">{value}</Typography>
                    </Box>
                  ) : null)}

                  {/* Payment Proof inline viewer */}
                  <Box>
                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                      Payment Slip:
                    </Typography>
                    {proofUrl ? (
                      <Box>
                        {/* Image preview */}
                        {isImage && (
                          <Box
                            component="img"
                            src={proofUrl}
                            alt="Payment proof"
                            sx={{ width: '100%', maxHeight: 400, objectFit: 'contain',
                                  border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                          />
                        )}
                        {/* PDF inline embed */}
                        {isPdf && (
                          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            <iframe
                              src={proofUrl}
                              title="Payment Slip"
                              width="100%"
                              height="450px"
                              style={{ border: 'none', display: 'block' }}
                            />
                          </Box>
                        )}
                        {/* Unknown type — just show download link */}
                        {!isImage && !isPdf && (
                          <Button variant="outlined" size="small" startIcon={<OpenInNew />}
                            onClick={() => window.open(proofUrl, '_blank')}>
                            Open Attachment
                          </Button>
                        )}
                        {/* Open in new tab link under preview */}
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          <Button size="small" startIcon={<OpenInNew />}
                            onClick={() => window.open(proofUrl, '_blank')}>
                            Open in New Tab
                          </Button>
                          <Button size="small" startIcon={<Download />}
                            component="a" href={proofUrl} download>
                            Download
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.disabled">No slip uploaded</Typography>
                    )}
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setViewDialog(false)}>Close</Button>
                <Button variant="outlined" color="secondary" startIcon={<NotificationsActive />}
                  onClick={() => { openNotifyForPayment(selected); }}>
                  Notify Buyer
                </Button>
                {selected.status === 'pending' && (
                  <>
                    <Button variant="outlined" color="error"
                      onClick={() => { handleReject(selected.id); setViewDialog(false); }}>
                      Reject
                    </Button>
                    <Button variant="contained" color="success"
                      onClick={() => { handleVerify(selected.id); setViewDialog(false); }}>
                      Verify Payment
                    </Button>
                  </>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
      {/* ── Notify Buyer Dialog ── */}
      <Dialog open={notifyDialog} onClose={() => { setNotifyDialog(false); setNotifyResult(null); }}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActive color="secondary" />
            <Typography variant="h6">Notify Buyer — Payment Update</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Compose a message. You'll get a WhatsApp link and buyer email to send it directly.
            </Alert>
            <TextField
              fullWidth multiline rows={4}
              label="Message to Buyer"
              value={notifyMsg}
              onChange={e => setNotifyMsg(e.target.value)}
              sx={{ mb: 2 }}
            />
            {notifyResult && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Alert severity="success">Links ready — choose how to send:</Alert>
                {notifyResult.whatsapp_link && (
                  <Button variant="contained" color="success" startIcon={<WhatsApp />} fullWidth
                    onClick={() => window.open(notifyResult.whatsapp_link, '_blank')}>
                    Open WhatsApp Chat
                  </Button>
                )}
                {notifyResult.buyer_email && (
                  <Button variant="outlined" color="primary" startIcon={<Email />} fullWidth
                    onClick={() => window.open(`mailto:${notifyResult.buyer_email}?subject=Payment Update&body=${encodeURIComponent(notifyMsg)}`, '_blank')}>
                    Send Email to {notifyResult.buyer_email}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNotifyDialog(false); setNotifyResult(null); }}>Close</Button>
          {!notifyResult && (
            <Button variant="contained" color="secondary" startIcon={<NotificationsActive />}
              onClick={handleNotify} disabled={notifying || !notifyMsg.trim()}>
              {notifying ? 'Loading...' : 'Get Notification Links'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default AdminPayments;